import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import { Add, Edit, Delete, Label } from '@mui/icons-material';
import { ChromePicker } from 'react-color';
import { tagsApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import TagChip from './TagChip';
import LoadingButton from './LoadingButton';

const TagManager = () => {
  const { showSuccess, showError } = useNotification();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState({ open: false, tag: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, tag: null });
  const [formData, setFormData] = useState({ name: '', color: '#FF6B6B' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      setLoading(true);
      const response = await tagsApi.getAll();
      setTags(response.data.tags || []);
    } catch (error) {
      console.error('Error loading tags:', error);
      showError('Failed to load tags');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditDialog = (tag = null) => {
    if (tag) {
      setFormData({ name: tag.name, color: tag.color });
      setEditDialog({ open: true, tag });
    } else {
      // Auto-assign color for new tag
      const colorPalette = [
        '#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8',
        '#F7DC6F', '#BB8FCE', '#85C1E2', '#F8B739', '#52B788'
      ];
      const nextColor = colorPalette[tags.length % colorPalette.length];
      setFormData({ name: '', color: nextColor });
      setEditDialog({ open: true, tag: null });
    }
  };

  const handleCloseEditDialog = () => {
    setEditDialog({ open: false, tag: null });
    setFormData({ name: '', color: '#FF6B6B' });
  };

  const handleSaveTag = async () => {
    if (!formData.name.trim()) {
      showError('Tag name is required');
      return;
    }

    setSaving(true);
    try {
      if (editDialog.tag) {
        // Update existing tag
        await tagsApi.update(editDialog.tag.id, formData);
        showSuccess('Tag updated successfully');
      } else {
        // Create new tag
        await tagsApi.create(formData);
        showSuccess('Tag created successfully');
      }
      handleCloseEditDialog();
      loadTags();
    } catch (error) {
      console.error('Error saving tag:', error);
      showError(error.response?.data?.message || 'Failed to save tag');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenDeleteDialog = (tag) => {
    setDeleteDialog({ open: true, tag });
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, tag: null });
  };

  const handleDeleteTag = async () => {
    setDeleting(true);
    try {
      await tagsApi.delete(deleteDialog.tag.id);
      showSuccess(`Tag "${deleteDialog.tag.name}" deleted successfully`);
      handleCloseDeleteDialog();
      loadTags();
    } catch (error) {
      console.error('Error deleting tag:', error);
      showError('Failed to delete tag');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box>
              <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                Manage Tags
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create and manage custom tags to organize your transactions
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenEditDialog()}
            >
              New Tag
            </Button>
          </Box>

          {tags.length === 0 ? (
            <Alert severity="info" icon={<Label />}>
              No tags created yet. Create your first tag to start organizing transactions.
            </Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Tag</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Color</TableCell>
                    <TableCell align="center">Transactions</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tags.map((tag) => (
                    <TableRow key={tag.id} hover>
                      <TableCell>
                        <TagChip tag={tag} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{tag.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              backgroundColor: tag.color,
                              border: '1px solid',
                              borderColor: 'divider'
                            }}
                          />
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {tag.color}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={tag.transactionCount || 0} 
                          size="small" 
                          color={tag.transactionCount > 0 ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenEditDialog(tag)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDeleteDialog(tag)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editDialog.tag ? 'Edit Tag' : 'Create New Tag'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Tag Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              autoFocus
              placeholder="Enter tag name (e.g., 'Important', 'Business', 'Recurring')"
            />
            
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tag Color
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <ChromePicker
                  color={formData.color}
                  onChange={(color) => setFormData({ ...formData, color: color.hex })}
                  disableAlpha
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Preview
                  </Typography>
                  <TagChip 
                    tag={{ name: formData.name || 'Tag Name', color: formData.color }} 
                    size="medium"
                  />
                </Box>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseEditDialog} disabled={saving}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleSaveTag}
            variant="contained"
            loading={saving}
          >
            {editDialog.tag ? 'Save Changes' : 'Create Tag'}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Tag</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Are you sure you want to delete the tag "{deleteDialog.tag?.name}"?
          </Typography>
          {deleteDialog.tag?.transactionCount > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This tag is currently assigned to {deleteDialog.tag.transactionCount} transaction(s). 
              Deleting it will remove the tag from all these transactions.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteTag}
            color="error"
            variant="contained"
            loading={deleting}
          >
            Delete Tag
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagManager;

